import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL, API_ENDPOINTS } from '@/config';
import { v4 as uuidv4 } from 'uuid';
import axiosInstance from '@/lib/enhancedAxiosInstance';
import { User, InvitationToken } from '@/types/team';
import { logError, logDebug, logInfo, logWarn } from '@/utils/errorHandler';

/* ──────────────────── types ──────────────────── */
interface AuthError {
  message: string;
  field?: string;
}

// --- Notification Types ---
export interface Notification {
  id: string;
  message: string;
  description?: string;
  timestamp: Date;
  read: boolean;
  type: 'info' | 'success' | 'error' | 'warning';
}

// Define an extended user type that includes role
interface ExtendedUser extends User {
  role?: 'admin' | 'editor' | 'viewer' | 'owner';
  is_staff?: boolean;
  is_superuser?: boolean;
  organization_id?: string;
  rolePermissions?: string[];
}

export interface AuthContextType {
  user: ExtendedUser | null;
  loading: boolean;
  error: AuthError | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    orgId?: string,
    token?: string
  ) => Promise<void>;
  clearAuthState: () => void;
  // --- Notification State and Functions ---
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markAllAsRead: () => void;
  updateUserContext: (updatedUserData: Partial<ExtendedUser>) => void;
  checkPermission: (permission: string) => boolean;
  checkPendingInvitation: () => void;
}

/* ──────────────────── React context ──────────────────── */
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Map of default permissions per role (can be used by checkPermission fallback)
const ROLE_PERMISSION_MAP = {
  admin: [
    'product.view',
    'product.add',
    'product.change',
    'product.delete',
    'team.view',
    'team.invite',
    'team.change_role',
    'team.remove',
    'dashboard.view',
    'reports.view',
    'analytics.view',
    'settings.view',
    'settings.change',
  ],
  owner: [
    'product.view',
    'product.add',
    'product.change',
    'product.delete',
    'team.view',
    'team.invite',
    'team.change_role',
    'team.remove',
    'dashboard.view',
    'reports.view',
    'analytics.view',
    'settings.view',
    'settings.change',
  ],
  editor: [
    'product.view',
    'product.add',
    'product.change',
    'team.view',
    'dashboard.view',
    'reports.view',
    'analytics.view',
  ],
  viewer: ['product.view', 'team.view', 'dashboard.view', 'reports.view', 'analytics.view'],
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ExtendedUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);
  const [logoutInProgress, setLogoutInProgress] = useState(false);
  // --- Notification State ---
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);

  // Update the normalizeUserData function to fetch role permissions
  const normalizeUserData = (data: any): ExtendedUser => {
    let role: 'admin' | 'editor' | 'viewer' | 'owner' | undefined = undefined;
    let rolePermissions: string[] | undefined = undefined;

    // Use role from backend if present (make case-insensitive)
    if (data.role) {
      const roleString = data.role.toString().toLowerCase();
      if (roleString === 'admin') role = 'admin';
      else if (roleString === 'editor') role = 'editor';
      else if (roleString === 'viewer') role = 'viewer';
      else if (roleString === 'orgowner' || roleString === 'owner') role = 'owner';
    } else if (data.memberships && data.memberships.length > 0) {
      const membership = data.memberships[0];
      if (membership.role) {
        // Make role comparison case-insensitive
        const roleName = membership.role.name?.toString().toLowerCase();
        if (roleName === 'admin') role = 'admin';
        else if (roleName === 'editor') role = 'editor';
        else if (roleName === 'viewer') role = 'viewer';
        else if (roleName === 'orgowner' || roleName === 'owner') role = 'owner';

        if (membership.role.permissions && Array.isArray(membership.role.permissions)) {
          rolePermissions = membership.role.permissions;
        }
      }
    }

    // If user has admin role but no specific permissions, set default admin permissions
    if (role === 'admin' && (!rolePermissions || rolePermissions.length === 0)) {
      rolePermissions = [
        'product.view',
        'product.add',
        'product.change',
        'product.delete',
        'dashboard.view',
        'team.view',
      ];
    }

    return {
      id: data.id,
      email: data.email,
      name: data.name || data.username,
      first_name: data.first_name,
      last_name: data.last_name,
      avatar_url: data.avatar_url,
      is_staff: Boolean(data.is_staff), // Use actual backend is_staff value
      role,
      rolePermissions,
      organization_id:
        data.organization_id ??
        data.organization?.id ??
        data.profile?.organization?.id ??
        (data.organizations && data.organizations.length > 0 ? data.organizations[0].id : null) ??
        (data.memberships && data.memberships.length > 0 && data.memberships[0].organization
          ? data.memberships[0].organization.id
          : null) ??
        null,
    };
  };

  // Update auth flow to use async IIFE and proper token handling
  useEffect(() => {
    (async () => {
      setLoading(true);
      const token = localStorage.getItem('access_token');

      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }

      // 1) Set Authorization header
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      try {
        // 2) Fetch user profile - this validates the token
        const meRes = await axiosInstance.get(API_ENDPOINTS.auth.user);

        if (!meRes.data || (Array.isArray(meRes.data) && meRes.data.length === 0)) {
          logError(
            'Empty user profile response',
            {
              component: 'AuthContext',
              action: 'initializeAuth',
              metadata: {
                hasData: !!meRes.data,
                isArray: Array.isArray(meRes.data),
              },
            },
            {
              showToast: true,
              userFacing: true,
            }
          );
          throw new Error('Failed to retrieve user profile');
        }

        // 3) Normalize user data
        const me = normalizeUserData(meRes.data);

        // 4) If we have an org ID, fetch memberships to get complete role and permission data
        if (me.id && me.organization_id) {
          try {
            const membershipsEndpoint = API_ENDPOINTS.orgs.memberships(me.organization_id);
            const memRes = await axiosInstance.get(membershipsEndpoint);

            const membership = Array.isArray(memRes.data)
              ? memRes.data.find((m: any) => {
                  // Find the user's membership
                  const userIdMatch = m.user?.id === me.id || m.user === me.id;
                  const userEmailMatch = m.user_email === me.email;
                  return userIdMatch || userEmailMatch;
                })
              : null;

            if (membership) {
              // Ensure the organization ID is preserved from the membership
              const orgId =
                membership.organization?.id ||
                (typeof membership.organization === 'string' ? membership.organization : null) ||
                me.organization_id;

              // Get role from membership with case-insensitive comparison
              let membershipRole: 'admin' | 'editor' | 'viewer' | 'owner' = 'viewer'; // Default fallback
              if (membership.role?.name) {
                const roleName = membership.role.name.toString().toLowerCase();
                if (roleName === 'admin') membershipRole = 'admin';
                else if (roleName === 'editor') membershipRole = 'editor';
                else if (roleName === 'viewer') membershipRole = 'viewer';
                else if (roleName === 'orgowner') membershipRole = 'owner';
              }

              // Get permissions from membership
              let membershipPermissions: string[] = [];
              if (membership.role?.permissions && Array.isArray(membership.role.permissions)) {
                membershipPermissions = membership.role.permissions;
              } else if (membershipRole === 'admin' || membershipRole === 'owner') {
                // Default admin/owner permissions if none specified
                membershipPermissions = [
                  'product.view',
                  'product.add',
                  'product.change',
                  'product.delete',
                  'dashboard.view',
                  'team.view',
                  'settings.view',
                  'settings.change',
                ];
              }

              // Set user with organization ID and role from membership
              setUser({
                ...me,
                organization_id: orgId,
                role: membershipRole,
                rolePermissions: membershipPermissions,
                is_staff: me.is_staff, // Use actual backend is_staff value
              });

              // Also set currentOrgId in localStorage for compatibility with other services
              if (orgId) {
                localStorage.setItem('currentOrgId', orgId.toString());
              }
            } else {
              logWarn('No matching membership found for user during initialization', {
                component: 'AuthContext',
                action: 'initializeAuth',
                metadata: {
                  userId: me.id,
                  organizationId: me.organization_id,
                  email: me.email,
                },
              });
              // Still set the user with basic data
              setUser(me);

              // Also set currentOrgId in localStorage if available
              if (me.organization_id) {
                localStorage.setItem('currentOrgId', me.organization_id.toString());
              }
            }
          } catch (membershipError) {
            logError(
              'Failed to fetch user memberships during initialization',
              {
                component: 'AuthContext',
                action: 'initializeAuth',
                metadata: {
                  userId: me.id,
                  organizationId: me.organization_id,
                  error: membershipError,
                },
              },
              {
                showToast: false,
                userFacing: false,
              }
            );
            // Still set the user with basic data
            setUser(me);

            // Also set currentOrgId in localStorage if available
            if (me.organization_id) {
              localStorage.setItem('currentOrgId', me.organization_id.toString());
            }
          }
        } else {
          logWarn('No organization ID found in user profile during initialization', {
            component: 'AuthContext',
            action: 'initializeAuth',
            metadata: {
              userId: me.id,
              email: me.email,
            },
          });
          // Set the user with available data
          setUser(me);
        }
      } catch {
        const inProtectedApp =
          typeof window !== 'undefined' && window.location.pathname.startsWith('/app');

        logError(
          'Authentication initialization failed',
          {
            component: 'AuthContext',
            action: 'initializeAuth',
            metadata: { hasToken: !!token },
          },
          {
            showToast: inProtectedApp, // suppress toast on public landing pages
            userFacing: inProtectedApp,
          }
        );

        // Clear ALL authentication data on any error
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('currentOrgId');
        delete axiosInstance.defaults.headers.common['Authorization'];
        setUser(null);
        setError(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Add logging for user state changes
  useEffect(() => {
    // Removed development logging to improve performance
  }, [user, loading]);

  // --- Notification Functions ---
  const addNotification = useCallback(
    (notificationData: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
      const newNotification: Notification = {
        ...notificationData,
        id: uuidv4(), // Generate unique ID
        timestamp: new Date(),
        read: false,
      };
      setNotifications(prev => [newNotification, ...prev].slice(0, 50)); // Add to start, limit to 50
      setUnreadCount(prev => prev + 1);

      // Optionally show a toast as well
      // toast[newNotification.type](newNotification.message, { description: newNotification.description });
    },
    []
  );

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  // Function to update user state locally
  const updateUserContext = useCallback((updatedUserData: Partial<ExtendedUser>) => {
    setUser(prevUser => {
      if (!prevUser) return null;
      const newUser = { ...prevUser, ...updatedUserData };

      // Also update currentOrgId in localStorage if organization_id changed
      if (updatedUserData.organization_id) {
        localStorage.setItem('currentOrgId', updatedUserData.organization_id.toString());
      }

      return newUser;
    });
  }, []);

  /* ─── login ─── */
  const login = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      // Log login attempt for important auth tracking
      logDebug('Login attempt initiated', { email });

      // In development, use relative URL for Vite proxy; in production, use full URL
      const isDevelopment = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';
      const loginUrl = isDevelopment
        ? API_ENDPOINTS.auth.login
        : `${API_URL}${API_ENDPOINTS.auth.login}`;

      // Try both email and username formats to handle different backend configurations
      const loginData = {
        email,
        username: email, // Some backends might expect username instead
        password,
      };

      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      // Clone the response for potential error handling
      const responseClone = response.clone();

      if (!response.ok) {
        let errorData;
        try {
          // Try to parse as JSON first
          errorData = await responseClone.json();
          logError(
            'Login request failed with JSON error response',
            {
              component: 'AuthContext',
              action: 'login',
              metadata: {
                status: response.status,
                errorData,
                email,
              },
            },
            {
              showToast: true,
              userFacing: true,
            }
          );
        } catch {
          // If the response isn't JSON, try text
          try {
            const errorText = await response.text();
            logError(
              'Login request failed with text error response',
              {
                component: 'AuthContext',
                action: 'login',
                metadata: {
                  status: response.status,
                  errorText,
                  email,
                },
              },
              {
                showToast: true,
                userFacing: true,
              }
            );
          } catch (textError) {
            logError(
              'Could not read error response',
              {
                component: 'AuthContext',
                action: 'login',
                metadata: {
                  status: response.status,
                  textError,
                  email,
                },
              },
              {
                showToast: true,
                userFacing: true,
              }
            );
          }
          throw new Error(`Login failed with status ${response.status}`);
        }

        // Handle specific error fields
        if (errorData.email) {
          throw new Error(errorData.email);
        } else if (errorData.password) {
          throw new Error(errorData.password);
        } else if (errorData.detail) {
          throw new Error(errorData.detail);
        } else if (errorData.error) {
          throw new Error(errorData.error);
        } else if (errorData.non_field_errors) {
          throw new Error(errorData.non_field_errors);
        }

        throw new Error(`Login failed with status ${response.status}`);
      }

      const data = await response.json();
      logInfo('Login successful', {
        component: 'AuthContext',
        action: 'login',
        metadata: {
          email,
          hasTokens: !!(data.access && data.refresh),
          hasUserData: !!data.user,
        },
      });

      // Update this to handle standard JWT token response
      if (data.access && data.refresh) {
        localStorage.setItem('access_token', data.access);
        localStorage.setItem('refresh_token', data.refresh);

        // Immediately set the Authorization header
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${data.access}`;

        try {
          // Check if the login response contains user data with organization and role
          if (data.user && typeof data.user === 'object') {
            // If the login response has organization_id and role, use them directly
            if (data.user.organization_id && data.user.role) {
              const userData = normalizeUserData(data.user);
              setUser({
                ...userData,
                organization_id: data.user.organization_id,
                role: data.user.role.toLowerCase(),
              });

              // Also set currentOrgId in localStorage for compatibility with other services
              if (data.user.organization_id) {
                localStorage.setItem('currentOrgId', data.user.organization_id.toString());
              }

              // Set session start time for session management
              localStorage.setItem('session_start', Date.now().toString());

              toast.success('Logged in successfully!');
              // Remove automatic navigation - let the calling component handle it
              // navigate('/app');
              return;
            }
          }

          // If user data is not in the login response or is incomplete, fetch the profile
          const userEndpoint = API_ENDPOINTS.auth.user;
          const userResponse = await axiosInstance.get(userEndpoint);

          if (
            !userResponse.data ||
            (Array.isArray(userResponse.data) && userResponse.data.length === 0)
          ) {
            throw new Error('Empty user profile response');
          }

          // Normalize user data
          const me = normalizeUserData(userResponse.data);

          // If we have an org ID, fetch memberships to determine role
          if (me.id && me.organization_id) {
            try {
              const membershipsEndpoint = API_ENDPOINTS.orgs.memberships(me.organization_id);
              const memRes = await axiosInstance.get(membershipsEndpoint);

              const membership = Array.isArray(memRes.data)
                ? memRes.data.find((m: any) => {
                    // More robust logic to find the user's membership
                    const userIdMatch = m.user?.id === me.id || m.user === me.id;
                    const userEmailMatch = m.user_email === me.email;
                    return userIdMatch || userEmailMatch;
                  })
                : null;

              if (membership) {
                // Ensure the organization ID is preserved from the membership
                const orgId =
                  membership.organization?.id ||
                  (typeof membership.organization === 'string' ? membership.organization : null) ||
                  me.organization_id;

                // Get role from membership with case-insensitive comparison
                let membershipRole: 'admin' | 'editor' | 'viewer' = 'viewer'; // Default fallback
                if (membership.role?.name) {
                  const roleName = membership.role.name.toString().toLowerCase();
                  if (roleName === 'admin') membershipRole = 'admin';
                  else if (roleName === 'editor') membershipRole = 'editor';
                  else if (roleName === 'viewer') membershipRole = 'viewer';
                }

                // Get permissions from membership
                let membershipPermissions: string[] = [];
                if (membership.role?.permissions && Array.isArray(membership.role.permissions)) {
                  membershipPermissions = membership.role.permissions;
                } else if (membershipRole === 'admin') {
                  // Default admin permissions if none specified
                  membershipPermissions = [
                    'product.view',
                    'product.add',
                    'product.change',
                    'product.delete',
                    'dashboard.view',
                    'team.view',
                  ];
                }

                // Set user with organization ID and role from membership
                setUser({
                  ...me,
                  organization_id: orgId,
                  role: membershipRole,
                  rolePermissions: membershipPermissions,
                  is_staff: me.is_staff, // Use actual backend is_staff value
                });

                // Also set currentOrgId in localStorage for compatibility with other services
                if (orgId) {
                  localStorage.setItem('currentOrgId', orgId.toString());
                }
              } else {
                logWarn('No matching membership found for user', {
                  component: 'AuthContext',
                  action: 'login',
                  metadata: {
                    userId: me.id,
                    organizationId: me.organization_id,
                    email: me.email,
                  },
                });
                // Still keep the organization_id we already found
                setUser(me);

                // Also set currentOrgId in localStorage if available
                if (me.organization_id) {
                  localStorage.setItem('currentOrgId', me.organization_id.toString());
                }
              }
            } catch {
              logError(
                'Failed to fetch user memberships',
                {
                  component: 'AuthContext',
                  action: 'login',
                  metadata: {
                    userId: me.id,
                    organizationId: me.organization_id,
                  },
                },
                {
                  showToast: false,
                  userFacing: false,
                }
              );
              setUser(me);

              // Also set currentOrgId in localStorage if available
              if (me.organization_id) {
                localStorage.setItem('currentOrgId', me.organization_id.toString());
              }
            }
          } else {
            logWarn('No organization ID found in user profile', {
              component: 'AuthContext',
              action: 'login',
              metadata: {
                userId: me.id,
                email: me.email,
              },
            });
            setUser(me);
          }

          // Set session start time for session management
          localStorage.setItem('session_start', Date.now().toString());

          // Check for pending invitations after successful login
          checkPendingInvitation();

          toast.success('Logged in successfully!');
          // Remove automatic navigation - let the calling component handle it
          // navigate('/app');
        } catch {
          logError(
            'Failed to fetch user profile after login',
            {
              component: 'AuthContext',
              action: 'login',
              metadata: { email },
            },
            {
              showToast: true,
              userFacing: true,
            }
          );
          // Clear tokens if we can't load the profile
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          delete axiosInstance.defaults.headers.common['Authorization'];
          setUser(null);
          throw new Error('Failed to load user profile');
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err: any) {
      logError(
        'Login failed',
        {
          component: 'AuthContext',
          action: 'login',
          metadata: {
            email,
            hasResponse: !!err.response,
            status: err.response?.status,
            errorMessage: err.message,
          },
        },
        {
          showToast: true,
          userFacing: true,
        }
      );

      const msg =
        err.response?.data?.detail || err.response?.data?.error || err.message || 'Login failed';
      setError({ message: msg });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /* ─── register ─── */
  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    orgId?: string,
    token?: string
  ) => {
    setLoading(true);
    setError(null);
    try {
      // Prepare the registration data
      const registrationData: any = {
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        password_confirm: password,
      };

      // Include organization ID and token if provided (for invited users)
      if (orgId) {
        registrationData.organization_id = orgId;
      }

      if (token) {
        registrationData.invitation_token = token;
      }

      // Log registration attempt for important auth tracking
      logDebug('Registration attempt initiated', { email, firstName, lastName });

      // In development, use relative URL for Vite proxy; in production, use full URL
      const isDevelopment = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';
      const registerUrl = isDevelopment
        ? API_ENDPOINTS.auth.register
        : `${API_URL}${API_ENDPOINTS.auth.register}`;

      const response = await axios.post(registerUrl, registrationData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      logInfo('Registration successful', {
        component: 'AuthContext',
        action: 'register',
        metadata: {
          email,
          hasTokens: !!(response.data.access && response.data.refresh),
          hasUserData: !!response.data.user,
        },
      });
      const { access, refresh, user: userData } = response.data;

      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);

      // Immediately set the Authorization header
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${access}`;

      // Normalize user data
      const normalizedUser = normalizeUserData(userData);
      setUser(normalizedUser);

      // Also set currentOrgId in localStorage if available
      if (normalizedUser.organization_id) {
        localStorage.setItem('currentOrgId', normalizedUser.organization_id.toString());
      }

      // Set session start time for session management
      localStorage.setItem('session_start', Date.now().toString());

      toast.success('Registered!');
      // navigate('/app');
    } catch (err: any) {
      logError(
        'Registration failed',
        {
          component: 'AuthContext',
          action: 'register',
          metadata: {
            email,
            firstName,
            lastName,
            hasOrganizationId: !!orgId,
            hasResponse: !!err.response,
            status: err.response?.status,
          },
        },
        {
          showToast: true,
          userFacing: true,
        }
      );

      // Enhanced error logging for API validation errors
      if (axios.isAxiosError(err) && err.response) {
        // Display specific field errors if available
        if (err.response.data && typeof err.response.data === 'object') {
          const errorData = err.response.data;
          let errorMessage = 'Registration failed: ';

          // Handle nested error objects or arrays
          Object.keys(errorData).forEach(key => {
            const value = errorData[key];
            if (Array.isArray(value)) {
              errorMessage += `${key}: ${value.join(', ')}. `;
            } else if (typeof value === 'object' && value !== null) {
              errorMessage += `${key}: ${JSON.stringify(value)}. `;
            } else {
              errorMessage += `${key}: ${value}. `;
            }
          });

          setError({ message: errorMessage });
          throw err;
        }
      }

      const msg =
        err.response?.data?.detail ||
        err.response?.data?.error ||
        err.message ||
        'Registration failed';
      setError({ message: msg });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /* ─── clearAuthState ─── */
  const clearAuthState = useCallback(() => {
    logDebug('Clearing authentication state', {
      component: 'AuthContext',
      action: 'clearAuthState',
    });
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('currentOrgId');
    setUser(null);
    setError(null);
    setLogoutInProgress(false);
    // Clear Axios header
    delete axiosInstance.defaults.headers.common['Authorization'];
  }, []);

  /* ─── logout ─── */
  const logout = useCallback(() => {
    if (logoutInProgress) {
      logDebug('Logout already in progress, skipping', {
        component: 'AuthContext',
        action: 'logout',
      });
      return;
    }

    logInfo('User logout initiated', {
      component: 'AuthContext',
      action: 'logout',
      metadata: { hasUser: !!user },
    });
    setLogoutInProgress(true);
    clearAuthState();
    toast.info('You have been logged out.');

    // Use setTimeout and replace to avoid navigation conflicts
    setTimeout(() => {
      window.location.replace('/');
    }, 100);
  }, [clearAuthState, logoutInProgress, user]);

  // Check if user has permission
  const checkPermission = (permission: string): boolean => {
    if (!user) {
      return false;
    }

    // Frontend-only fix: Handle category permissions until backend roles are updated
    if (permission.startsWith('category.')) {
      if (user.role === 'owner' || user.role === 'admin') {
        return true; // Full category access
      }
      if (
        user.role === 'editor' &&
        ['category.view', 'category.add', 'category.change'].includes(permission)
      ) {
        return true; // Editor can view, add, and change categories but not delete
      }
      if (user.role === 'viewer' && permission === 'category.view') {
        return true; // Viewer can only view categories
      }
    }

    // ORGANIZATION-BASED PERMISSIONS (Primary Logic)
    // Organization owners and admins have full organization permissions
    if (user.role === 'admin' || user.role === 'owner') {
      return true;
    }

    // Check direct permissions array from user's role (preferred method)
    if (user.rolePermissions && Array.isArray(user.rolePermissions)) {
      const hasPermission = user.rolePermissions.includes(permission);
      return hasPermission;
    }

    // Fallback to role-based checking if permissions array not available
    if (user.role === 'editor') {
      const editorPermissions = ROLE_PERMISSION_MAP.editor || [
        'product.view',
        'product.add',
        'product.change',
        'team.view',
        'dashboard.view',
        'reports.view',
        'analytics.view',
      ];
      const hasPermission = editorPermissions.includes(permission);
      return hasPermission;
    }

    if (user.role === 'viewer') {
      const viewerPermissions = ROLE_PERMISSION_MAP.viewer || [
        'product.view',
        'team.view',
        'dashboard.view',
        'reports.view',
        'analytics.view',
      ];
      const hasPermission = viewerPermissions.includes(permission);
      return hasPermission;
    }

    // Enhanced fallback: if no explicit role permissions but we have an admin/owner role
    // Grant common admin permissions to handle cases where rolePermissions isn't loaded yet
    if (user.role === 'admin' || user.role === 'owner') {
      const commonAdminPermissions = [
        'product.view',
        'product.add',
        'product.change',
        'product.delete',
        'dashboard.view',
        'team.view',
        'team.invite',
        'team.change_role',
        'team.remove',
        'settings.view',
        'settings.change',
        'reports.view',
        'analytics.view',
      ];
      if (commonAdminPermissions.includes(permission)) {
        return true;
      }
    }

    // DJANGO ADMIN PERMISSIONS (Only for system-level access)
    // Django staff only gets system admin permissions, NOT organization permissions
    if (user.is_staff && permission.startsWith('admin.')) {
      return true;
    }

    return false;
  };

  // Check for pending invitations
  const checkPendingInvitation = () => {
    const pendingInviteStr = sessionStorage.getItem('pendingInvitation');

    if (pendingInviteStr) {
      try {
        const pendingInvite: InvitationToken = JSON.parse(pendingInviteStr);

        // Clear the stored invitation
        sessionStorage.removeItem('pendingInvitation');

        // Redirect to the accept invitation page
        if (pendingInvite.membershipId && pendingInvite.token) {
          window.location.href = `/accept-invite/${pendingInvite.membershipId}/${pendingInvite.token}`;
        }
      } catch (error) {
        logError(
          'Failed to process pending invitation',
          {
            component: 'AuthContext',
            action: 'checkPendingInvitation',
            metadata: { error },
          },
          {
            showToast: false,
            userFacing: false,
          }
        );
      }
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: Boolean(user), // Simplified isAuthenticated
    login,
    logout,
    register,
    // --- Provide notification state and functions ---
    notifications,
    unreadCount,
    addNotification,
    markAllAsRead,
    updateUserContext,
    checkPermission,
    checkPendingInvitation,
    clearAuthState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* ───────── custom hook ───────── */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
